import { useState, useEffect, useCallback } from 'react';

// SPEC-102: a single freshness verdict for the whole UI. All staleness decisions
// come from the backend (/api/governance/freshness `critical_feeds`), never from a
// frontend-local time threshold — the header dot, page strips and Governance tab
// must all agree with the demand-planning banner.
export interface CriticalFeedVerdict {
  stale: boolean;
  maxDate: string | null;
  daysStale: number | null;
  thresholdDays: number | null;
}

interface FreshnessState {
  lastSync: string | null;
  syncStatus: 'ok' | 'error' | 'unknown';
  syncAge: string; // display metadata only (portal sync age) — NOT a staleness verdict
  isStale: boolean; // backend-driven: any critical feed stale OR sync unhealthy
  tablesFreshness: Record<string, string | null>;
  // SPEC-132: per-table age (whole days since the table's max date), keyed by table
  // name. Drives PageFreshnessStrip when a page declares the source table(s) it reads,
  // so a page on stale data shows an honest amber/red strip instead of a green dot.
  tableDaysStale: Record<string, number | null>;
  loading: boolean;
  // Demand-signal staleness (order feed the forecast runs on), SPEC-085.
  demandSignalStale: boolean;
  demandSignalMaxDate: string | null;
  demandSignalDaysStale: number | null;
  // Stock-signal staleness (live-stock feed), SPEC-102.
  stockSignalStale: boolean;
  stockSignalMaxDate: string | null;
  stockSignalDaysStale: number | null;
  // Any critical feed stale — the app-wide "data is stale" verdict.
  criticalStale: boolean;
  // Full per-feed verdicts as returned by the backend, keyed by table name.
  criticalFeeds: Record<string, CriticalFeedVerdict>;
  // SPEC-517 (G62): when the forecast was last published and the next weekly publication.
  // `message` is the backend's one sentence (null under the nightly cadence); `missed` is the
  // backend's cadence-aware verdict — never a threshold computed here.
  forecastPublication: { message: string | null; missed: boolean } | null;
}

function formatAge(ms: number): string {
  if (ms < 0) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function parseFeed(raw: any): CriticalFeedVerdict {
  return {
    stale: raw ? !!raw.is_stale : false,
    maxDate: raw ? (raw.max_date ?? null) : null,
    daysStale: raw && typeof raw.days_stale === 'number' ? raw.days_stale : null,
    thresholdDays: raw && typeof raw.threshold_days === 'number' ? raw.threshold_days : null,
  };
}

const INITIAL: FreshnessState = {
  lastSync: null,
  syncStatus: 'unknown',
  syncAge: '',
  isStale: false,
  tablesFreshness: {},
  tableDaysStale: {},
  loading: true,
  demandSignalStale: false,
  demandSignalMaxDate: null,
  demandSignalDaysStale: null,
  stockSignalStale: false,
  stockSignalMaxDate: null,
  stockSignalDaysStale: null,
  criticalStale: false,
  criticalFeeds: {},
  forecastPublication: null,
};

export function useFreshness(): FreshnessState {
  const [state, setState] = useState<FreshnessState>(INITIAL);

  const fetchFreshness = useCallback(async () => {
    try {
      const token = localStorage.getItem('suryaiq_token');
      if (!token) return;
      const res = await fetch('/api/governance/freshness', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setState(prev => ({ ...prev, syncStatus: 'error', loading: false }));
        return;
      }
      const data = await res.json();

      // Extract last_sync from pipeline or portal sync status
      const pipelineSync = data.pipeline?.last_sync || null;
      const portalSync = data.external_feeds?.surya_portal?.sync_status?.synced_at || null;
      const lastSync = portalSync || pipelineSync;

      // Build per-table freshness map
      const tablesFreshness: Record<string, string | null> = {};
      const tableDaysStale: Record<string, number | null> = {};
      if (data.tables) {
        for (const [name, info] of Object.entries(data.tables) as [string, any][]) {
          tablesFreshness[name] = info.max_date || null;
          tableDaysStale[name] = typeof info.days_stale === 'number' ? info.days_stale : null;
        }
      }

      const age = lastSync ? Date.now() - Date.parse(lastSync) : 0;
      const pipelineStatus = data.pipeline?.status;
      const syncStatus: 'ok' | 'error' | 'unknown' =
        pipelineStatus === 'failed' ? 'error' :
        (pipelineStatus === 'success' || pipelineStatus === 'portal_sync_active') ? 'ok' :
        'unknown';

      // Backend critical-feed verdicts (SPEC-085/102): the age of the demand + stock
      // signals the forecast actually runs on. These — not portal sync age — drive
      // every staleness colour in the UI so a frozen feed can't hide behind a green dot.
      const criticalFeeds: Record<string, CriticalFeedVerdict> = {};
      if (data.critical_feeds) {
        for (const [name, info] of Object.entries(data.critical_feeds) as [string, any][]) {
          criticalFeeds[name] = parseFeed(info);
        }
      }
      const demand = criticalFeeds['fact_orders'] ?? parseFeed(null);
      const stock = criticalFeeds['fact_stock_enhanced'] ?? parseFeed(null);
      const criticalStale = Object.values(criticalFeeds).some(f => f.stale);

      setState({
        lastSync,
        syncStatus,
        syncAge: lastSync ? formatAge(age) : '',
        // "Stale" = a backend verdict, no local threshold: any critical feed frozen,
        // or the sync pipeline itself unhealthy.
        isStale: criticalStale || syncStatus === 'error',
        tablesFreshness,
        tableDaysStale,
        loading: false,
        demandSignalStale: demand.stale,
        demandSignalMaxDate: demand.maxDate,
        demandSignalDaysStale: demand.daysStale,
        stockSignalStale: stock.stale,
        stockSignalMaxDate: stock.maxDate,
        stockSignalDaysStale: stock.daysStale,
        criticalStale,
        criticalFeeds,
        forecastPublication: data.forecast_publication
          ? { message: data.forecast_publication.message ?? null,
              missed: !!data.forecast_publication.missed }
          : null,
      });
    } catch {
      setState(prev => ({ ...prev, syncStatus: 'error', loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchFreshness();
    const interval = setInterval(fetchFreshness, 300_000); // 5 min
    return () => clearInterval(interval);
  }, [fetchFreshness]);

  // Keep the displayed sync age fresh every minute without re-fetching. Staleness
  // verdicts come from the backend, so only the humanised label needs re-ticking.
  useEffect(() => {
    if (!state.lastSync) return;
    const tick = setInterval(() => {
      const age = Date.now() - Date.parse(state.lastSync!);
      setState(prev => ({ ...prev, syncAge: formatAge(age) }));
    }, 60_000);
    return () => clearInterval(tick);
  }, [state.lastSync]);

  return state;
}
